import { supabase } from '../lib/supabase'

// =====================================================
// TOPICS
// =====================================================

export async function getAllTopics() {
  const { data, error } = await supabase
    .from('topics')
    .select(`
      *,
      profiles:created_by (
        id,
        first_name,
        last_name
      ),
      questions:questions(count)
    `)
    .order('is_featured', { ascending: false })
    .order('question_count', { ascending: false })

  if (error) throw error
  
  // Map the actual question count from the join
  return data.map(topic => ({
    ...topic,
    question_count: topic.questions?.[0]?.count || 0
  }))
}

export async function getTopicBySlug(slug) {
  const { data, error } = await supabase
    .from('topics')
    .select(`
      *,
      profiles:created_by (
        id,
        first_name,
        last_name
      )
    `)
    .eq('slug', slug)
    .single()

  if (error) throw error
  return data
}

export async function createTopic(topicData) {
  // Validate required fields
  if (!topicData.created_by) {
    throw new Error('User ID is required to create a topic')
  }
  
  // Validate UUID format
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
  if (!uuidRegex.test(topicData.created_by)) {
    throw new Error(`Invalid user ID format: ${topicData.created_by}`)
  }

  const { data, error } = await supabase
    .from('topics')
    .insert([topicData])
    .select()
    .single()

  if (error) {
    console.error('Error fetching topic:', error)
    throw error
  }
  return data
}

// =====================================================
// QUESTIONS
// =====================================================

export async function getQuestionsByTopic(topicSlugOrId, sortBy = 'recent') {
  // First get the topic to get its ID
  let topicId = topicSlugOrId
  
  // Check if it's a valid UUID format
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
  const isUUID = uuidRegex.test(topicSlugOrId)
  
  // If it's not a UUID, treat it as a slug and fetch the topic first
  if (!isUUID) {
    const topic = await getTopicBySlug(topicSlugOrId)
    topicId = topic.id
  }

  let query = supabase
    .from('questions')
    .select(`
      *,
      profiles:author_id (
        id,
        first_name,
        last_name,
        avatar_url
      ),
      topics (
        id,
        name,
        slug
      ),
      answers:answers(count)
    `)
    .eq('topic_id', topicId)

  // Apply sorting
  if (sortBy === 'recent') {
    query = query.order('created_at', { ascending: false })
  } else if (sortBy === 'popular') {
    query = query.order('vote_count', { ascending: false })
  } else if (sortBy === 'unanswered') {
    query = query.eq('is_answered', false).order('created_at', { ascending: false })
  }

  const { data, error } = await query

  if (error) throw error
  
  // Transform data to include answer_count
  return data.map(question => ({
    ...question,
    answer_count: question.answers?.[0]?.count || 0
  }))
}

export async function getQuestionById(questionId) {
  const { data, error} = await supabase
    .from('questions')
    .select(`
      *,
      profiles:author_id (
        id,
        first_name,
        last_name,
        avatar_url
      ),
      topics (
        id,
        name,
        slug
      )
    `)
    .eq('id', questionId)
    .single()

  if (error) throw error
  
  // Increment view count
  await supabase
    .from('questions')
    .update({ view_count: data.view_count + 1 })
    .eq('id', questionId)

  return data
}

export async function createQuestion(questionData) {
  const { data, error } = await supabase
    .from('questions')
    .insert([questionData])
    .select()
    .single()

  if (error) throw error

  // Increment topic question count
  await supabase.rpc('increment', {
    table_name: 'topics',
    row_id: questionData.topic_id,
    column_name: 'question_count'
  })

  return data
}

export async function updateQuestion(questionId, updates) {
  const { data, error } = await supabase
    .from('questions')
    .update(updates)
    .eq('id', questionId)
    .select()
    .single()

  if (error) throw error
  return data
}

export async function deleteQuestion(questionId) {
  // Get question data first to decrement topic count
  const { data: questionData } = await supabase
    .from('questions')
    .select('topic_id')
    .eq('id', questionId)
    .single()

  const { error } = await supabase
    .from('questions')
    .delete()
    .eq('id', questionId)

  if (error) throw error

  // Decrement topic question count
  if (questionData?.topic_id) {
    const { data: topicData } = await supabase
      .from('topics')
      .select('question_count')
      .eq('id', questionData.topic_id)
      .single()

    await supabase
      .from('topics')
      .update({ 
        question_count: Math.max(0, (topicData?.question_count || 0) - 1)
      })
      .eq('id', questionData.topic_id)
  }
}

// =====================================================
// ANSWERS
// =====================================================

export async function getAnswersByQuestion(questionId) {
  const { data, error } = await supabase
    .from('answers')
    .select(`
      *,
      profiles:author_id (
        id,
        first_name,
        last_name,
        avatar_url
      )
    `)
    .eq('question_id', questionId)
    .order('is_best_answer', { ascending: false })
    .order('vote_count', { ascending: false })

  if (error) throw error
  return data
}

export async function createAnswer(answerData) {
  const { data, error } = await supabase
    .from('answers')
    .insert([answerData])
    .select()
    .single()

  if (error) throw error

  // Increment question answer count - fetch current count first
  const { data: questionData } = await supabase
    .from('questions')
    .select('answer_count, author_id, title, topic_id, topics(slug), profiles:author_id(email, first_name, last_name)')
    .eq('id', answerData.question_id)
    .single()

  await supabase
    .from('questions')
    .update({ 
      answer_count: (questionData?.answer_count || 0) + 1,
      is_answered: true 
    })
    .eq('id', answerData.question_id)

  // Send notification email to question author (don't wait for it)
  if (questionData && questionData.profiles?.email && questionData.author_id !== answerData.author_id) {
    // Get answer author name
    const { data: answerAuthor } = await supabase
      .from('profiles')
      .select('first_name, last_name')
      .eq('id', answerData.author_id)
      .single()

    if (answerAuthor) {
      sendAnswerNotificationEmail({
        questionTitle: questionData.title,
        questionId: answerData.question_id,
        topicSlug: questionData.topics.slug,
        answerAuthorName: `${answerAuthor.first_name} ${answerAuthor.last_name}`,
        answerContent: answerData.content,
        questionAuthorEmail: questionData.profiles.email
      }).catch(error => {
        console.error('Failed to send answer notification email:', error)
        // Don't throw - answer is already created
      })
    }
  }

  return data
}

/**
 * Send answer notification email
 * @param {Object} data - Answer notification data
 * @returns {Promise<void>}
 */
async function sendAnswerNotificationEmail(data) {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    const token = session?.access_token

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-answer-notification-email`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token || import.meta.env.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify(data)
      }
    )

    if (!response.ok) {
      const error = await response.json()
      throw new Error(`Email function error: ${JSON.stringify(error)}`)
    }

    console.log('Answer notification email sent successfully')
  } catch (error) {
    console.error('Error sending answer notification email:', error)
    throw error
  }
}

export async function updateAnswer(answerId, updates) {
  const { data, error } = await supabase
    .from('answers')
    .update(updates)
    .eq('id', answerId)
    .select()
    .single()

  if (error) throw error
  return data
}

export async function deleteAnswer(answerId) {
  // Get answer data first to decrement question count
  const { data: answerData } = await supabase
    .from('answers')
    .select('question_id')
    .eq('id', answerId)
    .single()

  const { error } = await supabase
    .from('answers')
    .delete()
    .eq('id', answerId)

  if (error) throw error

  // Decrement question answer count
  if (answerData?.question_id) {
    const { data: questionData } = await supabase
      .from('questions')
      .select('answer_count')
      .eq('id', answerData.question_id)
      .single()

    const newCount = Math.max(0, (questionData?.answer_count || 0) - 1)

    await supabase
      .from('questions')
      .update({ 
        answer_count: newCount,
        is_answered: newCount > 0
      })
      .eq('id', answerData.question_id)
  }
}

export async function markBestAnswer(questionId, answerId) {
  // Remove best answer from other answers
  await supabase
    .from('answers')
    .update({ is_best_answer: false })
    .eq('question_id', questionId)

  // Mark this answer as best
  const { data, error } = await supabase
    .from('answers')
    .update({ is_best_answer: true })
    .eq('id', answerId)
    .select()
    .single()

  if (error) throw error

  // Update question with best answer
  await supabase
    .from('questions')
    .update({ best_answer_id: answerId })
    .eq('id', questionId)

  return data
}

// =====================================================
// VOTES
// =====================================================

export async function voteQuestion(questionId, voteType) {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) throw new Error('Must be logged in to vote')

  // Check if user already voted - use maybeSingle() instead of single()
  const { data: existingVote } = await supabase
    .from('votes')
    .select('*')
    .eq('user_id', user.id)
    .eq('question_id', questionId)
    .maybeSingle()

  if (existingVote) {
    if (existingVote.vote_type === voteType) {
      // Remove vote if clicking same button
      const { error } = await supabase
        .from('votes')
        .delete()
        .eq('id', existingVote.id)
      
      if (error) throw error
      return null
    } else {
      // Update vote type
      const { data, error } = await supabase
        .from('votes')
        .update({ vote_type: voteType })
        .eq('id', existingVote.id)
        .select()
        .single()
      
      if (error) throw error
      return data
    }
  } else {
    // Create new vote
    const { data, error } = await supabase
      .from('votes')
      .insert([{
        user_id: user.id,
        question_id: questionId,
        vote_type: voteType
      }])
      .select()
      .single()

    if (error) throw error
    return data
  }
}

export async function voteAnswer(answerId, voteType) {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) throw new Error('Must be logged in to vote')

  // Check if user already voted - use maybeSingle() instead of single()
  const { data: existingVote } = await supabase
    .from('votes')
    .select('*')
    .eq('user_id', user.id)
    .eq('answer_id', answerId)
    .maybeSingle()

  if (existingVote) {
    if (existingVote.vote_type === voteType) {
      // Remove vote if clicking same button
      const { error } = await supabase
        .from('votes')
        .delete()
        .eq('id', existingVote.id)
      
      if (error) throw error
      return null
    } else {
      // Update vote type
      const { data, error } = await supabase
        .from('votes')
        .update({ vote_type: voteType })
        .eq('id', existingVote.id)
        .select()
        .single()
      
      if (error) throw error
      return data
    }
  } else {
    // Create new vote
    const { data, error } = await supabase
      .from('votes')
      .insert([{
        user_id: user.id,
        answer_id: answerId,
        vote_type: voteType
      }])
      .select()
      .single()

    if (error) throw error
    return data
  }
}

export async function getUserVotes(userId, questionIds = [], answerIds = []) {
  const { data, error } = await supabase
    .from('votes')
    .select('*')
    .eq('user_id', userId)
    .or(`question_id.in.(${questionIds.join(',')}),answer_id.in.(${answerIds.join(',')})`)

  if (error) throw error
  return data
}
